import { FC } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { Box, Flex } from '@chakra-ui/react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import OpenGraph from '@/components/OpenGraph';
import Fallback from '@/components/Fallback';
import FullPage from '@/src/components/UI/FullPage';
import CopyIcon from '@/src/components/UI/CopyIcon';
import { isServer } from '@/src/libs/is-next-server';
import useT from '@/lib/t';
import FunwooAPI, { JobEntity } from '@/swagger';
import { GetStaticProps } from 'next';
import { AxiosError } from 'axios';

const NavBar = ({ id }: { id: string }) => {
  const t = useT('pages.job');
  return (
    <div className={'flex w-full h-16  flex-row justify-between items-center mt-16 max-w-screen-lg px-8'}>
      <div
        onClick={() => {
          window.history.back();
        }}
        className={'flex flex-row items-center cursor-pointer'}
      >
        <Image width={24} height={24} className={'w-6 h-6 mx-4'} src={'/arrow-left.svg'} />
        <div className={'font-medium text-lg'}>{t('more')}</div>
      </div>
      <div className={'flex flex-row items-center'}>
        {!isServer() && <CopyIcon text={`${process.env.NEXT_PUBLIC_HOSTNAME}/jobs/${id}`} />}
      </div>
    </div>
  );
};

interface Props {
  job: JobEntity;
  source: MDXRemoteSerializeResult;
}

const JobsContent: FC<Props> = ({ job, source }) => {
  const router = useRouter();
  if (router.isFallback) {
    return <Fallback />;
  }
  const { title, sid } = job;
  const t = useT('pages.job');
  return (
    <FullPage hasHeaderPlaceHolder title={title ?? ''}>
      <OpenGraph
        title={t('ogTitle', { title })}
        type='funwoo.career'
        url={`https://funwoo.com.tw/jobs/${sid}`}
        imageUrl={`${process.env.NEXT_PUBLIC_GOOGLE_CDN_URL}/mobile/largeImages/hero-img-desktop/hero-careers-wide-desktop.webp`}
      />
      <Flex display={{ base: 'none', sm: 'flex' }} direction={'column'} w={'100%'} alignSelf={'center'}>
        <NavBar id={sid} />
      </Flex>
      <Box className='markdown-body'>
        <MDXRemote {...source} />
      </Box>
    </FullPage>
  );
};

// noinspection JSUnusedGlobalSymbols
export async function getStaticPaths({ locales }: { locales: Array<string> }) {
  const { data: jobs } = await FunwooAPI.jobApi.findAll();
  const paths = jobs.map((job) => locales.map((locale) => ({ locale, params: { jobId: job.sid } }))).flat();
  return {
    paths,
    fallback: true,
  };
}

export const getStaticProps: GetStaticProps<Props, { jobId: string }> = async ({
  locale = '',
  params: { jobId } = { jobId: '' },
}) => {
  const { data: job } = await FunwooAPI.jobApi.findOne(jobId).catch((error: AxiosError) => {
    if (error.response?.status === 409) return { data: null };
    else throw error;
  });
  if (!job) {
    return {
      notFound: true,
      revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
    };
  }
  const source = `
## Who we are
FUNWOO 團隊致力於結合科技、設計與專業房產顧問來媒合買賣雙方，簡化房產交易流程，在台輕鬆置產美國 。

在頂尖工程師與資料分析師的共同努力下，FUNWOO讓買賣雙方一手掌握最新數據，建構出智能無縫的檢索與銷售體驗；專業設計與行銷團隊提供合適的銷售、媒體策略，將客戶的物件以最完善的方式呈現；專業房產顧問，運用豐沛的平台資源及專業洞察，依客戶需求提供更高效的服務。

對FUNWOO而言，每一位房產顧問都是我們的企業夥伴， FUNWOO 作為創新的房地產平台，樂見所有優秀的同業及異業人才加入我們的行列，成功經營自己的理想！我們正在一步步地改變房產交易的繁瑣過程，並希望以此連接成緊密的信任關係。

## Responsibilities
${job.jd}

## Qualifications
${job.qualification}

${
  job.offer
    ? `## Salary & Support
${job.offer}
`
    : ''
}

請 email 相關履歷資料至：career@funwoo.com.tw

如有任何應徵問題，請撥打 02-7752-3755
  `;
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      // job: serializer(job),
      // source: await serialize(source),
    },
    notFound: true,
    revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
  };
};

export default JobsContent;
